import ApiError from '@legacy/js/model/Error'; // todo replace legacy error handling with new ApiError
import { format, isValid, parse, parseISO } from 'date-fns';

export interface SchoolYear {
    id: number
    kindergartenId: number
    name: string
    start: string
    end: string
    immutableDate: string
    isEditable: boolean
    isDeletable: boolean
}

export interface SchoolYearForm {
    id?: number
    kindergartenId: number
    name: string
    start: string
    end: string
    immutableDate: string
}

export function createEmpty(kindergartenId: number): SchoolYearForm {
    return {
        kindergartenId,
        name: '',
        start: '',
        end: '',
        immutableDate: '',
    };
}

export function isEmpty(schoolYear: SchoolYearForm): boolean {
    return !schoolYear.name && !schoolYear.start && !schoolYear.end && !schoolYear.immutableDate;
}

interface SchoolYearApiResponse {
    id: number
    kindergarten_id: number
    name: string
    start: string | null
    end: string | null
    immutable_date: string | null
    is_editable: boolean
    is_deletable: boolean
}

function formatDate(date: string | null | undefined): string {
    if (!date) {
        return '';
    }
    const parsed = parseISO(date);
    return isValid(parsed) ? format(parsed, 'dd.MM.yyyy') : '';
}

async function handleResponse(response: Response): Promise<void> {
    if (!response.ok) {
        const data = await response.json();
        throw ApiError.fromResponse({ status: response.status, data });
    }
}

export async function findAll(kindergartenId: number): Promise<SchoolYear[]> {
    const response = await fetch(`/api/kindergartens/${kindergartenId}/school-years`, {
        headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch school years: ${response.status}`);
    }

    const json = await response.json() as { data: SchoolYearApiResponse[] };
    return json.data.map((sy) => ({
        id: sy.id,
        kindergartenId: sy.kindergarten_id,
        name: sy.name,
        start: formatDate(sy.start),
        end: formatDate(sy.end),
        immutableDate: formatDate(sy.immutable_date),
        isEditable: sy.is_editable,
        isDeletable: sy.is_deletable,
    }));
}

export async function create(name: string, start: string, end: string, immutableDate: string): Promise<void> {
    const response = await fetch('/api/school-years', {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            name,
            start: format(parse(start, 'dd.MM.yyyy', new Date()), 'yyyy-MM-dd'),
            end: format(parse(end, 'dd.MM.yyyy', new Date()), 'yyyy-MM-dd'),
            immutableDate: format(parse(immutableDate, 'dd.MM.yyyy', new Date()), 'yyyy-MM-dd'),
        }),
    });

    await handleResponse(response);
}

export async function update(schoolYear: SchoolYearForm): Promise<void> {
    const response = await fetch(`/api/kindergarten/${schoolYear.kindergartenId}/school-years/${schoolYear.id}`, {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            name: schoolYear.name,
            start: format(parse(schoolYear.start, 'dd.MM.yyyy', new Date()), 'yyyy-MM-dd'),
            end: format(parse(schoolYear.end, 'dd.MM.yyyy', new Date()), 'yyyy-MM-dd'),
            immutableDate: format(parse(schoolYear.immutableDate, 'dd.MM.yyyy', new Date()), 'yyyy-MM-dd'),
        }),
    });

    await handleResponse(response);
}

export async function remove(schoolYearId: number): Promise<void> {
    const response = await fetch(`/api/school-years/${schoolYearId}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
    });

    await handleResponse(response);
}
